import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Camera, X, ChevronLeft, ChevronRight, Heart, ZoomIn, ZoomOut, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePinchZoom } from '@/hooks/usePinchZoom';
import { useGsapFlipImage } from '@/hooks/useGsapFlipImage';
import { useGsapDraggableCarousel } from '@/hooks/useGsapDraggableCarousel';


interface Props {
  photos: string[];
  title: string;
  videoUrl?: string | null;
  onToggleSave?: () => void;
  saved?: boolean;
  containFirstPhoto?: boolean;
}

/** Extrage ID-ul YouTube din watch, youtu.be, shorts sau embed. */
export const getYouTubeId = (url?: string | null): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([A-Za-z0-9_-]{6,})/,
    /(?:youtu\.be\/)([A-Za-z0-9_-]{6,})/,
    /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{6,})/,
    /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/,
    /(?:youtube\.com\/live\/)([A-Za-z0-9_-]{6,})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

export const GalleryV2: React.FC<Props> = ({ photos, title, videoUrl, onToggleSave, saved, containFirstPhoto = false }) => {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const main = photos[active] || photos[0];
  const mainImgRef = useRef<HTMLImageElement>(null);

  // FLIP: imaginea „se mută" din cardul de listare în galerie.
  useGsapFlipImage(mainImgRef, active === 0 ? main : null);

  // Swipe cu inerție (Draggable + InertiaPlugin) pe fotografia principală.
  const { viewportRef, trackRef, wasDraggedRef } = useGsapDraggableCarousel({
    count: photos.length,
    index: active,
    onIndexChange: setActive,
    enabled: !showVideo,
  });



  const youtubeId = getYouTubeId(videoUrl);
  const fileVideo = !youtubeId && videoUrl && (/\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i.test(videoUrl) || videoUrl.includes('/storage/v1/object/'))
    ? videoUrl
    : null;
  const hasVideo = !!(youtubeId || fileVideo);


  const { scale, translateX, translateY, isZoomed, handlers, resetZoom, toggleZoom } = usePinchZoom(1, 4);

  // Reset zoom when changing image or closing
  useEffect(() => { resetZoom(); }, [active, open]);

  const next = () => { resetZoom(); setActive((i) => (i + 1) % photos.length); };
  const prev = () => { resetZoom(); setActive((i) => (i - 1 + photos.length) % photos.length); };


  // Keyboard nav in modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, photos.length]);

  if (!photos.length) {
    if (hasVideo) {
      return (
        <div className="relative overflow-hidden bg-black -mx-4 md:mx-0 md:rounded-2xl aspect-[16/10]">
          {youtubeId ? (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title={`Video ${title}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
            />
          ) : (
            <video src={fileVideo!} controls playsInline className="w-full h-full object-contain" />
          )}
        </div>
      );
    }

    return (
      <div className="aspect-[16/10] rounded-2xl bg-muted flex items-center justify-center text-muted-foreground">
        Fără imagini
      </div>
    );
  }

  return (
    <div>
      <div className="relative overflow-hidden bg-muted -mx-4 md:mx-0 md:rounded-2xl">
        {showVideo && hasVideo ? (
          <div className="w-full h-[240px] md:h-auto md:aspect-[16/10] bg-black">
            {youtubeId ? (
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                title={`Video ${title}`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <video src={fileVideo!} controls autoPlay muted playsInline className="w-full h-full object-contain" />
            )}
          </div>
        ) : (

          <div
            ref={viewportRef}
            role="button"
            tabIndex={0}
            aria-label="Deschide galeria foto"
            onClick={() => {
              if (wasDraggedRef.current) return;
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setOpen(true);
            }}
            className="relative w-full h-[240px] md:h-auto md:aspect-[16/10] overflow-hidden cursor-pointer"
          >
            <div
              ref={trackRef}
              className="flex h-full will-change-transform"
              style={{ width: `${photos.length * 100}%` }}
            >
              {photos.map((p, i) => (
                <div key={i} className="h-full" style={{ width: `${100 / photos.length}%` }}>
                  <img
                    ref={i === 0 ? mainImgRef : undefined}
                    src={p}
                    alt={i === 0 ? title : `${title} ${i + 1}`}
                    draggable={false}
                    className={`w-full h-full ${containFirstPhoto && i === 0 ? 'object-contain' : 'object-cover'} select-none pointer-events-none`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="absolute top-3 right-3 flex gap-2">
          {onToggleSave && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onToggleSave}
              className="bg-background/95 backdrop-blur shadow-sm"
            >
              <Heart className={`h-4 w-4 mr-1 ${saved ? 'fill-accent text-accent' : ''}`} />
              Salvează
            </Button>
          )}
        </div>
        {!showVideo && (
          <button
            onClick={() => setOpen(true)}
            className="absolute bottom-3 right-3 bg-foreground/80 text-background text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 backdrop-blur"
          >
            <Camera className="h-3.5 w-3.5" /> {photos.length} foto
          </button>
        )}
      </div>

      {/* Thumbnails: horizontal scroll on mobile, grid on desktop */}
      <div className="mt-3 flex md:grid md:grid-cols-4 gap-2 overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory scrollbar-hide">
        {hasVideo && (
          <button
            onClick={() => setShowVideo(true)}
            aria-label="Vezi videoclipul anunțului"
            className={`relative flex-none w-20 h-16 md:w-auto md:h-auto md:aspect-[4/3] rounded-lg overflow-hidden bg-black border-2 snap-start transition ${
              showVideo ? 'border-primary' : 'border-transparent'
            }`}
          >
            {youtubeId ? (
              <img
                src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
                alt={`Video ${title}`}
                className="w-full h-full object-cover opacity-80"
                loading="lazy"
              />
            ) : (
              <img src={photos[0]} alt={`Video ${title}`} className="w-full h-full object-cover opacity-60" loading="lazy" />
            )}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="p-1.5 rounded-full bg-background/90">
                <Play className="h-4 w-4 text-foreground fill-foreground" />
              </span>
            </span>
          </button>
        )}

        {photos.slice(0, 8).map((p, i) => (
          <button
            key={i}
            onClick={() => { setShowVideo(false); setActive(i); }}
            className={`flex-none w-20 h-16 md:w-auto md:h-auto md:aspect-[4/3] rounded-lg overflow-hidden bg-muted border-2 snap-start transition ${
              i === active && !showVideo ? 'border-primary' : 'border-transparent'
            } ${i >= (hasVideo ? 3 : 4) ? 'md:hidden' : ''}`}
          >

            <img src={p} alt={`${title} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl p-0 bg-background">
          <div
            className="relative aspect-[16/10] bg-black overflow-hidden touch-none select-none"
            {...handlers}
          >
            <img
              src={photos[active]}
              alt={title}
              draggable={false}
              onDoubleClick={toggleZoom}
              style={{
                transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: isZoomed ? 'none' : 'transform 0.2s ease-out',
                cursor: isZoomed ? 'zoom-out' : 'zoom-in',
              }}
              className="w-full h-full object-contain"
            />

            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 p-2 bg-foreground/60 text-background rounded-full z-10"
              aria-label="Închide"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              onClick={toggleZoom}
              className="absolute top-3 right-14 p-2 bg-foreground/60 text-background rounded-full z-10"
              aria-label={isZoomed ? 'Micșorează' : 'Mărește'}
            >
              {isZoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
            </button>

            {photos.length > 1 && !isZoomed && (
              <>
                <button onClick={prev} className="absolute top-1/2 left-3 -translate-y-1/2 p-2 bg-foreground/60 text-background rounded-full z-10" aria-label="Anterior">
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button onClick={next} className="absolute top-1/2 right-3 -translate-y-1/2 p-2 bg-foreground/60 text-background rounded-full z-10" aria-label="Următor">
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-foreground/60 text-background text-xs rounded-full">
                  {active + 1} / {photos.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
